import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import { fetchThemeData } from '../admin/themeSlice';
import { setSession } from '../admin/sessionSlice';
import { setCompanyLogoUrl } from './uiSlice';

const uuid4 = () => {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
      const r = (Math.random() * 16) | 0;
      const v = c === 'x' ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    });
  };

function defaultBackButtonRedirect() {
  return (
    process.env.REACT_APP_BASE_URL ||
    (typeof window !== 'undefined' ? window.location.origin : '/') ||
    '/'
  );
}

async function restoreStoredUser(stored, dispatch) {
  const userData = JSON.parse(stored);
  if (!userData?.sessionId || !userData.organizationId) {
    return null;
  }
  // Saved sessions expire (expiry is written at login); an expired one must log in again.
  if (userData.expiry && Number(userData.expiry) < Date.now()) {
    return null;
  }
  dispatch(setSession({ sessionId: userData.sessionId, organizationId: userData.organizationId }));
  try {
    await dispatch(fetchThemeData({
      themeId: null,
      isCopy: !!userData.isCopy,
      originalSessionId: userData.originalSessionId || null,
    })).unwrap();
    if (userData.isCopy) {
      userData.isCopy = false;
      userData.originalSessionId = null;
      localStorage.setItem('userData', JSON.stringify(userData));
    }
  } catch (themeErr) {
    console.warn('Theme fetch failed while restoring session.', themeErr);
  }
  dispatch(setCompanyLogoUrl(userData.companyLogoUrl || process.env.REACT_APP_EM_LOGO));
  return userData;
}

export const loginUser = createAsyncThunk(
  'auth/loginUser',
  async (_, { dispatch, rejectWithValue }) => {
    try {
      const queryParams = new URLSearchParams(window.location.search);
      const save = queryParams.get('save');
      const demo = queryParams.get('demo');
      const otp = queryParams.get('otp');

      if (save) {
        const stored = localStorage.getItem('userData');
        if (!stored) return rejectWithValue('No saved user data found.');
        const userData = await restoreStoredUser(stored, dispatch);
        if (!userData) return rejectWithValue('Saved session is invalid. Please log in again.');
        return userData;
      }

      if (!otp && !demo) {
        const stored = localStorage.getItem('userData');
        if (stored) {
          try {
            const userData = await restoreStoredUser(stored, dispatch);
            if (userData) return userData;
          } catch {
            /* fall through to OTP login */
          }
        }
        return rejectWithValue('OTP is required for login.');
      }

      let userResponseData;
      if (demo) {
        function generateRandomUser() {
          const randomString = 'demo' + Math.floor(Math.random() * 10000);
          return {
            data: {
              data: {
                id: Math.floor(Math.random() * 100000),
                email: `${randomString}@gmail.com`,
                userId: randomString,
                firstName: randomString,
                lastName: randomString,
                employeeId: `EM${Math.floor(Math.random() * 1000).toString().padStart(3, '0')}`,
                sessionId: 'demo'+Math.floor(Math.random() * 100000),
                organizationId: 'demo'+Math.floor(Math.random() * 100000),
                gameId: null,
                role:"demobypass",
                companyLogoUrl: process.env.REACT_APP_EM_LOGO,
                backButtonRedirect: defaultBackButtonRedirect(),
              }
            }
          };
        }
        const response = generateRandomUser();
        userResponseData = response.data.data;
      } else {
        const role = queryParams.get('role');

        let loginApiUrl = process.env.REACT_APP_LOGIN_API;
        const requestData = { otp };
        let source = 'ORG_USER';
        if (role === 'GUEST_USER') {
          loginApiUrl = process.env.REACT_APP_GUEST_USER_API;
          requestData.role = 'GUEST_USER';
          source = 'GUEST_USER';
        }

        if (!loginApiUrl) {
          return rejectWithValue('Login API is not configured (REACT_APP_LOGIN_API).');
        }

        const headers = {
          'client-secret': process.env.REACT_APP_CLIENT_SECRET,
          'Content-Type': 'application/json',
          'Idempotency-Key': uuid4(),
        };

        const response = await axios.post(loginApiUrl, requestData, { headers });
        if (response.data.message !== 'LOGIN SUCCESS') {
          return rejectWithValue('Login failed');
        }
        response.data.data["role"]=source;
        userResponseData = response.data.data;
      }

      // Store session and fetch theme (copy from original when session was duplicated)
      const wasCopy = !!userResponseData.isCopy;
      const originalSessionId = userResponseData.originalSessionId || null;
      dispatch(setSession({ sessionId: userResponseData.sessionId, organizationId: userResponseData.organizationId }));
      let copyPending = wasCopy && !!originalSessionId;
      try {
        await dispatch(fetchThemeData({
          themeId: null,
          isCopy: wasCopy,
          originalSessionId,
        })).unwrap();
        copyPending = false;
      } catch (themeErr) {
        console.warn('Theme fetch failed after login; will retry on next load if this is a copy.', themeErr);
      }

      // Build user data to persist
      const userData = {
        userId: userResponseData.userId || userResponseData.id,
        token: userResponseData.token || "",
        name: `${userResponseData.firstName || ''} ${userResponseData.lastName || ''}`.trim()
          || (userResponseData.email ? userResponseData.email.split('@')[0] : '')
          || userResponseData.employeeId
          || userResponseData.userId
          || '',
        email: userResponseData.email || '',
        employeeId: userResponseData.employeeId || '',
        sessionId: userResponseData.sessionId,
        organizationId: userResponseData.organizationId,
        gameId: userResponseData.gameId,
        source: demo ? 'DEMO' : (userResponseData.role === 'GUEST_USER' ? 'GUEST_USER' : 'ORG_USER'),
        expiry: Date.now() + 24 * 60 * 60 * 1000,
        role:userResponseData.role,
        companyLogoUrl: process.env.REACT_APP_EM_LOGO,
        backButtonRedirect: defaultBackButtonRedirect(),
        userPlayedCount:userResponseData.userPlayedCount,
        // Keep copy flags only if theme seed/copy failed so restore can retry
        isCopy: copyPending,
        originalSessionId: copyPending ? originalSessionId : null,
      };

      // Fetch organization details if not demo (non-fatal if org API is missing or fails)
      if (!demo) {
        try {
          const orgApi = process.env.REACT_APP_ORG_API;
          if (!orgApi) {
            console.warn('REACT_APP_ORG_API is not set; skipping organization fetch.');
          } else {
            const orgRes = await axios.get(`${orgApi}/${userResponseData.organizationId}`);
            if (orgRes.data?.message === 'ORGANIZATIONS_FETCHED_SUCCESSFULLY') {
              const logoPath = orgRes.data?.organization?.companyLogo;
              if (!logoPath || logoPath === '') {
                userData.companyLogoUrl = process.env.REACT_APP_EM_LOGO;
              } else if (/^https?:\/\//i.test(String(logoPath))) {
                userData.companyLogoUrl = logoPath;
              } else {
                const base = (
                  process.env.REACT_APP_S3_SOURCE ||
                  ''
                ).replace(/\/+$/, '');
                userData.companyLogoUrl = base
                  ? `${base}/${String(logoPath).replace(/^\//, '')}`
                  : process.env.REACT_APP_EM_LOGO;
              }
              const baseUrl = defaultBackButtonRedirect().replace(/\/+$/, '');
              if (userResponseData.gameId) {
                userData.backButtonRedirect = `${baseUrl}/game-detail/${userResponseData.gameId}`;
              }
            }
          }
        } catch (orgErr) {
          console.warn('Organization fetch failed; using default login settings.', orgErr);
        }
      }
      
      dispatch(setCompanyLogoUrl(userData.companyLogoUrl || process.env.REACT_APP_EM_LOGO));
      localStorage.setItem('userData', JSON.stringify(userData));
      return userData;
    } catch (error) {
      return rejectWithValue(error.response?.data?.err?.message || error.message);
    }
  }
);
const persisted = localStorage.getItem('userData');
const initialUser = persisted ? JSON.parse(persisted) : null;

const authSlice = createSlice({
  name: 'auth',
  initialState: {
    user: initialUser,         // <-- hydrate from storage
    status: initialUser ? 'succeeded' : 'idle',
    error: null
  },
  reducers: {
    logout: state => {
      state.user = null;
      // Clear every app key so the next player on this browser starts clean.
      const LOCAL_KEYS = ['userData', 'session', 'currentTheme'];
      const LOCAL_PREFIXES = ['yoga_session_', 'yoga_first_upload_', 'tot_report_persist_'];
      const SESSION_KEYS = ['jurySession', 'yoga_score', 'yoga_time_sec', 'yoga_matched_names', 'spotThankYouMsg'];
      try {
        LOCAL_KEYS.forEach((k) => localStorage.removeItem(k));
        Object.keys(localStorage)
          .filter((k) => LOCAL_PREFIXES.some((p) => k.startsWith(p)))
          .forEach((k) => localStorage.removeItem(k));
      } catch {
        /* ignore */
      }
      try {
        SESSION_KEYS.forEach((k) => sessionStorage.removeItem(k));
        Object.keys(sessionStorage)
          .filter((k) => k.startsWith('tot_report_'))
          .forEach((k) => sessionStorage.removeItem(k));
      } catch {
        /* ignore */
      }
    }
  },
  extraReducers: builder => {
    builder
      .addCase(loginUser.pending, state => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(loginUser.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.user = action.payload;
      })
      .addCase(loginUser.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload;
      })
      .addCase(fetchThemeData.fulfilled, (state, action) => {
        if (!action.payload?._clearedCopy || !state.user?.isCopy) return;
        state.user = {
          ...state.user,
          isCopy: false,
          originalSessionId: null,
        };
        try {
          const stored = localStorage.getItem('userData');
          if (stored) {
            const userData = JSON.parse(stored);
            if (userData.isCopy) {
              userData.isCopy = false;
              userData.originalSessionId = null;
              localStorage.setItem('userData', JSON.stringify(userData));
            }
          }
        } catch {
          /* ignore */
        }
      });
  }
});

export const { logout } = authSlice.actions;
export default authSlice.reducer;
